import { CANCELED_API_REQ } from '@commutifi-fe/constants'
import { useCallback } from 'react'
import { useSafeSetState } from './global'

interface UseAsyncFnPropsOptions {
  onSuccess?: (res: any) => void
  shouldThrow?: boolean
  initialState?: {
    loaded: boolean
    loading: boolean
    error: any
    data: any
  }
}

interface UseAsyncFnProps {
  fn: ((...args: any) => Promise<any>) | undefined
  deps?: any[]
  opts?: UseAsyncFnPropsOptions
}

const DEFAULT_OPTIONS = { shouldThrow: false, onSuccess: (data: any) => data }
/**
 * @deprecated in favor of react-query
 * This custom hook is useful when you want to have the control on when you
 * want to trigger the api call. This can be useful for api call methods
 * PUT, PATCH, POST, DELETE, etc.
 *
 * @param {fn} fn: api function to call (Must return a Promise or be an async function)
 * @example 1: useAsyncFn(fetch) where fetch is a promise.
 * @example 2: useAsyncFn(async () => { async work .... })
 *
 * @param {array} deps: array of hook dependencies to trigger a new api call
 * @param {object} opts: { onSuccess: called to process the api call result data,
 *                         shouldThrow: throw an error (true) or return nothing (false),
 *                         initialState: overwrite default initial state }
 * @return {array} [0]: state containing the api status props
 *                 [1]: triggerApiCall function to pass the params and call fn
 */
export const useAsyncFn = (
  fn: UseAsyncFnProps['fn'] = () => new Promise<void>((resolve) => resolve()),
  deps: UseAsyncFnProps['deps'] = [],
  opts: UseAsyncFnProps['opts'] = {}
) => {
  const [state, setState] = useSafeSetState({
    loaded: false,
    loading: false,
    error: null,
    data: null,
    ...opts.initialState
  })

  const options = { ...DEFAULT_OPTIONS, ...opts }
  const triggerApiCall = useCallback((...args: any) => {
    setState({ loading: true, loaded: false, error: null })
    return fn
      ? fn(...args)
          .then((res) => {
            if (res === CANCELED_API_REQ) {
              return
            }

            const normalizedRes = options.onSuccess(
              res &&
                typeof res === 'object' &&
                Object.prototype.hasOwnProperty.call(res, 'data')
                ? res.data
                : res
            )
            setState({
              loaded: true,
              loading: false,
              error: null,
              data: normalizedRes
            })
            return normalizedRes
          })
          .catch((error: unknown) => {
            setState({ loaded: false, loading: false, error, data: null })
            // The reason we don't return anything is that we don't want
            // to force the user to wrap in a try catch and force him to use the
            // error set in state. Also, we know that if this hook return a value
            // it will be the resolved response of the function
            // Unless we want to await the error and display a message easily
            if (options.shouldThrow) {
              throw error
            }
          })
      : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return [state, triggerApiCall]
}

export default useAsyncFn
